import IOSInstallCTA from '../components/IOSInstallCTA';
import PersistentPushAction from '../components/PersistentPushAction';
import { PushReminderSlot } from '../components/PushPermissionSheet';
import { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext, apiClient } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Users, MapPin, LogOut, Plus, User, Shield, SlidersHorizontal, ListChecks, HelpCircle, Briefcase, Users2, Lock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatLocalDisplay, userTz, getUserTimezone, isPastRun, parseUTC } from '../utils/datetime';
import { deriveRunLifecycle, RUN_LIFECYCLE } from '../utils/runLifecycle';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tzPlugin from 'dayjs/plugin/timezone';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(utc);
dayjs.extend(tzPlugin);
dayjs.extend(isBetween);
import { normalizeApiError } from '../utils/normalizeError';
import { tzDebugOn, getPreviewTz } from '../utils/previewTz';
import { formatAgeRange } from '../utils/ageRange';
import LocationSearchInput from '../components/LocationSearchInput';
// import PeopleToFollow from '../components/PeopleToFollow'; // top carousel hidden (reversible) — see render site below
import NotificationBell from '../components/NotificationBell';
import PlayerSuggestions from '../components/PlayerSuggestions';
import Footer from '../components/Footer';
import { getRunNoun } from '../utils/runNoun';
import { usePendingInvitations } from '../components/PendingInvitations';
import { askOnMyFeed } from '../lib/pushPromptManager';
import { getParticipantCount } from '../utils/participantCount';

const Dashboard = () => {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Feed state
  const [feedData, setFeedData] = useState({
    games: [],
    counts: { my_games: 0, following: 0, discover: 0 },
    user_location_set: false
  });
  const [loading, setLoading] = useState(true);
  const identity = useRef({ id: user?.id, token, generation: 0 });
  if (identity.current.id !== user?.id || identity.current.token !== token) {
    identity.current = { id: user?.id, token, generation: identity.current.generation + 1 };
  }
  const generation = identity.current.generation;
  const requests = useRef(0);
  const [feedGeneration, setFeedGeneration] = useState(-1);
  // Presentation provenance only: Discover data is not a resolved My Feed.
  const recommendationFeed = useRef(null);
  const [invitationFocus, setInvitationFocus] = useState(false);
  const invitations = usePendingInvitations({ onJoined: () => fetchPersonalizedFeed() });
  useEffect(() => {
    if (invitationFocus && selectedDate === null) {
      document.querySelector('[data-testid="invitations-group"]')?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
      setInvitationFocus(false);
    }
  });
  const [filterOpen, setFilterOpen] = useState(false);
  
  // Filters for discovery mode
  const [filters, setFilters] = useState({
    skill_level: '',
    court_type: '',
    status: 'open',
  });
  const [locInput, setLocInput] = useState('');
  const [near, setNear] = useState(null);
  const [radiusKm, setRadiusKm] = useState(25);
  
  // View mode: 'feed' (personalized) or 'discover' (filtered search)
  const [viewMode, setViewMode] = useState('feed');
  if (viewMode !== 'feed') recommendationFeed.current = null;
  useEffect(() => { if (viewMode === 'feed' && !loading) askOnMyFeed(); }, [viewMode, loading, user?.id]);
  
  // Today filter state
  const [selectedDate, setSelectedDate] = useState(null); // null = all dates

  // Build the 5-day date strip (Today + next 4 days)
  const userTimezone = getUserTimezone();
  const dateTiles = Array.from({ length: 5 }, (_, i) => {
    const d = dayjs().tz(userTimezone).add(i, 'day').startOf('day');
    return {
      key: d.format('YYYY-MM-DD'),
      label: i === 0 ? 'Today' : d.format('ddd'),
      date: d.format('D'),
      month: d.format('MMM'),
      dayjs: d,
    };
  });

  // Filter games by selected date
  const filterByDate = (games) => {
    if (!selectedDate) return games;
    const selDay = dayjs(selectedDate).tz(userTimezone).startOf('day');
    const selEnd = selDay.endOf('day');
    return games.filter(g => {
      if (!g.date_time) return false;
      const gameLocal = dayjs(parseUTC(g.date_time)).tz(userTimezone);
      return gameLocal.isBetween(selDay, selEnd, null, '[]');
    });
  };

  useEffect(() => {
    if (viewMode === 'feed') {
      fetchPersonalizedFeed();
    } else {
      fetchFilteredGames();
    }
  }, [viewMode, filters, locInput, near, radiusKm, user?.id, token]);

  const fetchPersonalizedFeed = async () => {
    // Stricter guard: require React state AND localStorage to confirm auth before
    // calling protected /feed. Prevents transient "Not authenticated" toast on
    // iOS WKWebView when localStorage lags behind React state during refresh/resume.
    let storedToken;
    try { storedToken = localStorage.getItem('access_token'); } catch { storedToken = null; console.warn('dashboard_storage_unavailable operation=read'); }
    if (!user || !token || !storedToken) {
      setViewMode('discover');
      return;
    }
    
    const g = identity.current.generation;
    const request = ++requests.current;
    try {
      setLoading(true);
      const res = await apiClient.get('/feed');
      if (identity.current.generation !== g || requests.current !== request) return;
      setFeedGeneration(g);
      recommendationFeed.current = { generation: g, data: res.data };
      setFeedData(res.data);
    } catch (err) {
      if (identity.current.generation !== g || requests.current !== request) return;
      console.error('Error fetching feed:', err);
      setViewMode('discover');
      // Suppress raw 401 toast — response interceptor handles re-auth/logout.
      // Showing the backend's "Not authenticated" detail to users is noise.
      if (err.response?.status !== 401) {
        toast.error(normalizeApiError(err, 'Failed to load personalized feed'));
      }
    } finally {
      if (identity.current.generation === g && requests.current === request) setLoading(false);
    }
  };
  
  // Get filtered games for display
  // Main feed must not behave like a history view: in feed mode, exclude PAST runs
  // (including past cancelled) using the existing canonical past boundary (isPastRun).
  // We filter strictly by date — NOT by status — so UPCOMING cancelled runs remain
  // visible (they are not past), preserving the cancellation notice in My Games for
  // joined/host/waitlisted users. Discover mode is left untouched.
  const currentGames = feedGeneration === generation ? feedData.games : [];
  // Invitation fields are mapped explicitly; a duplicate feed record is never spread into them.
  const invitationGame = (row, joined = false) => ({
    id: row.game_id, title: row.game_title, date_time: row.game_date_time,
    court_type: row.game_type === 'training' ? 'training' : 'run',
    location_name: [row.game_location || 'Location shared after joining', row.game_city].filter(Boolean).join(' · '),
    creator_username: row.host_username, participant_count: row.participant_count,
    max_players: row.max_players, status: 'open', feed_category: joined ? 'joined' : 'invited',
    invitation: joined ? null : row, invitationJoined: joined,
  });
  const pendingIds = new Set(invitations.rows.map(row => String(row.game_id)));
  const joinedIds = new Set(currentGames.filter(game => ['joined', 'created', 'cohosted'].includes(game.feed_category)).map(game => String(game.id)));
  const transitionIds = new Set(invitations.joinedRows.filter(row => !joinedIds.has(String(row.game_id))).map(row => String(row.game_id)));
  const merged = [...currentGames.filter(game => !pendingIds.has(String(game.id)) && !transitionIds.has(String(game.id))),
    ...invitations.rows.map(row => invitationGame(row)),
    ...invitations.joinedRows.filter(row => !joinedIds.has(String(row.game_id)) && !pendingIds.has(String(row.game_id)))
      .map(row => invitationGame(row, true))];
  const dateFilteredGames = filterByDate(viewMode === 'feed' ? merged : currentGames);
  const displayGames = viewMode === 'feed'
    ? dateFilteredGames.filter((g) => !isPastRun(g.date_time))
    : dateFilteredGames;

  // Count the full returned upcoming window, before date-chip filtering.
  // A background read does not invalidate a successful same-account snapshot.
  // Keep invitation provenance separate: failed reads may discard hook rows.
  const recommendationInvitations = useRef(null);
  if (invitations.loading === false && !invitations.error) {
    recommendationInvitations.current = { generation, rows: invitations.rows };
  }
  const recommendationRows = recommendationInvitations.current?.generation === generation
    ? recommendationInvitations.current.rows : [];
  const recommendationPendingIds = new Set(recommendationRows.map(row => String(row.game_id)));
  const actionableIds = new Set();
  const upcoming = game => {
    const start = parseUTC(game.date_time);
    return start && Number.isFinite(start.getTime()) && start.getTime() > Date.now()
      && ['open', 'confirmed', 'full', 'waitlist_available'].includes(game.status)
      && deriveRunLifecycle(game) === RUN_LIFECYCLE.ACTIVE;
  };
  [...currentGames, ...invitations.joinedRows.map(row => invitationGame(row, true))].forEach(game => {
    if (game.id == null || !upcoming(game)) return;
    const participating = ['created', 'cohosted', 'joined'].includes(game.feed_category)
      || game.invitationJoined || game.creator_id === user?.id
      || (game.co_host_ids || []).some(id => String(id) === String(user?.id))
      || [...(game.team_a_players || []), ...(game.team_b_players || [])].some(p => p.user_id === user?.id);
    // Pending cards replace unjoined feed duplicates, including capacity rejections.
    if (!participating && recommendationPendingIds.has(String(game.id))) return;
    const min = game.age_min ?? 0, max = game.age_max ?? 99;
    const eligible = Number.isFinite(user?.age) && Number.isFinite(min) && Number.isFinite(max)
      && user.age >= min && user.age <= max;
    const capacity = game.max_players || 10;
    const filled = game.participant_count ?? (game.spots_filled || getParticipantCount(game));
    if (participating || (eligible && (game.feed_category === 'waitlisted'
      || (Number.isFinite(capacity) && Number.isFinite(filled) && filled < capacity)))) {
      actionableIds.add(String(game.id));
    }
  });
  recommendationRows.forEach(row => {
    if (row.game_id != null && upcoming(invitationGame(row)) && row.can_accept
      && !row.is_full && !invitations.capacityRejected(row)) actionableIds.add(String(row.game_id));
  });
  const recommendationsKnown = feedGeneration === generation
    && recommendationFeed.current?.generation === generation
    && recommendationFeed.current.data === feedData && Array.isArray(feedData.games)
    && recommendationInvitations.current?.generation === generation;
  const showRecommendations = viewMode === 'discover'
    || (recommendationsKnown && actionableIds.size < 2);

  const fetchFilteredGames = async () => {
    const g = identity.current.generation;
    const request = ++requests.current;
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (near && near.lat && near.lng) {
        params.append('near_lat', near.lat);
        params.append('near_lng', near.lng);
        params.append('radius_km', radiusKm);
      } else if (locInput.trim()) {
        params.append('q', locInput.trim());
      }
      
      if (filters.skill_level && filters.skill_level !== 'all') {
        params.append('skill_level', filters.skill_level);
      }
      if (filters.court_type && filters.court_type !== 'all') {
        params.append('court_type', filters.court_type);
      }
      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }

      const res = await apiClient.get(`/games?${params.toString()}`);
      if (identity.current.generation !== g || requests.current !== request) return;
      setFeedGeneration(g);
      setFeedData({
        games: res.data,
        counts: { my_games: 0, following: 0, discover: res.data.length },
        user_location_set: false
      });
    } catch (err) {
      if (identity.current.generation !== g || requests.current !== request) return;
      console.error('Error fetching games:', err);
      toast.error(normalizeApiError(err, 'Failed to load games'));
    } finally {
      if (identity.current.generation === g && requests.current === request) setLoading(false);
    }
  };

  // Debug: Show timezone info if enabled
  const debugTz = tzDebugOn();
  const previewTz = getPreviewTz();

  // Helper: Parse UTC date string safely
  const parseUtcDate = (dateStr) => {
    if (!dateStr) return null;
    const str = String(dateStr);
    // Handle both formats: with timezone suffix (Z or +00:00) or without
    if (str.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(str)) {
      return new Date(str);
    }
    return new Date(str + 'Z');
  };

  // Count active filters
  const activeFilterCount = [
    filters.skill_level && filters.skill_level !== 'all',
    filters.court_type && filters.court_type !== 'all',
    filters.status && filters.status !== 'open',
    locInput.trim() || near,
  ].filter(Boolean).length;

  // Group games by category for feed view
  // User age for filtering
  const userAge = user?.age || 99;
  const isMinor = userAge < 18;
  
  // Helper to check age eligibility
  const isAgeEligibleForGame = (game) => {
    const gameAgeMin = game.age_min || 0;
    const gameAgeMax = game.age_max || 99;
    return userAge >= gameAgeMin && userAge <= gameAgeMax;
  };
  
  // Separate games by category using filtered games
  // For minors: remove age-ineligible runs from My Feed entirely
  const myGames = displayGames.filter(g => {
    const isMyGame = ['created', 'cohosted', 'joined', 'waitlisted'].includes(g.feed_category);
    if (!isMyGame) return false;
    // Always show games user created or joined, even if age changed
    if (['created', 'cohosted', 'joined'].includes(g.feed_category)) return true;
    // For minors, filter out age-ineligible from waitlisted
    if (isMinor && !isAgeEligibleForGame(g)) return false;
    return true;
  });
  
  myGames.sort((a, b) => parseUTC(a.date_time) - parseUTC(b.date_time) || String(a.id).localeCompare(String(b.id)));

  const invitedGames = displayGames.filter(game => game.feed_category === 'invited')
    .sort((a, b) => parseUTC(a.date_time) - parseUTC(b.date_time) || String(a.id).localeCompare(String(b.id)));

  // Following games - for minors, filter out age-ineligible
  const followingGames = displayGames.filter(g => {
    if (g.feed_category !== 'following') return false;
    if (isMinor && !isAgeEligibleForGame(g)) return false;
    return true;
  });
  
  // Discover games - show all, but locked for ineligible (handled in card)
  const discoverGames = displayGames.filter(g => g.feed_category === 'discover');

  // Format full date/time for card: "FRI · MAY 15 · 3:00 PM"
  const formatCardDateTime = (dateStr) => {
    try {
      if (!dateStr) return 'TBD';
      const date = parseUtcDate(dateStr);
      if (!date || isNaN(date.getTime())) return 'TBD';
      const tz = getUserTimezone();
      const day = date.toLocaleDateString('en-US', { weekday: 'short', timeZone: tz }).toUpperCase();
      const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: tz }).toUpperCase();
      const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz });
      return `${day} · ${monthDay} · ${time}`;
    } catch {
      return 'TBD';
    }
  };

  // Render a single game card
  const GameCard = ({ game }) => {
    const invitation = game.invitation;
    const rejected = invitation && invitations.capacityRejected(invitation);
    const playerCount = getParticipantCount(game);
    const maxPlayers = game.max_players || 10;
    const spotsFilled = game.participant_count ?? (game.spots_filled || playerCount);
    const spotsLeft = maxPlayers - spotsFilled;
    const isFull = spotsLeft <= 0;
    
    const isJoined = game.invitationJoined || (user && (
      (game.team_a_players || []).some(p => p.user_id === user.id) ||
      (game.team_b_players || []).some(p => p.user_id === user.id)
    ));
    const isCreator = user && game.creator_id === user.id;
    const isCoHost = user && (game.co_host_ids || []).some(id => String(id) === String(user.id));
    const isManagedByUser = isCreator || isCoHost;
    const coHosts = game.co_hosts || [];
    
    // Age eligibility check
    const gameAgeMin = game.age_min || 0;
    const gameAgeMax = game.age_max || 99;
    const isAgeEligible = userAge >= gameAgeMin && userAge <= gameAgeMax;
    const isAgeRestricted = !invitation && !isAgeEligible && !isManagedByUser && !isJoined;
    
    const spotsText = isFull ? 'Full' : `${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left`;
    const hostName = game.creator_username || 'Unknown';

    // Viewer-independent lifecycle: cancelled > completed > awaiting_outcome > active.
    const lifecycle = deriveRunLifecycle(game);
    const isActive = lifecycle === RUN_LIFECYCLE.ACTIVE;
    // Noun source of truth: centralized getRunNoun (existing court_type field).
    const runNoun = getRunNoun(game);
    const isCancelled = lifecycle === RUN_LIFECYCLE.CANCELLED;
    const hasResult = !!game.result_winner || game.completion_status === 'completed';
    // Past/cancelled/completed runs never show "spots left"/"Full" — show a label instead.
    const lifecycleLabel =
      lifecycle === RUN_LIFECYCLE.CANCELLED ? 'Cancelled'
      : lifecycle === RUN_LIFECYCLE.COMPLETED ? 'Completed'
      : lifecycle === RUN_LIFECYCLE.AWAITING_OUTCOME ? 'Awaiting outcome'
      : null;

    // "Today" detection — uses user's local timezone (same pattern as dateTiles).
    // Rolls over at user's local midnight, not UTC. Only ACTIVE runs are highlighted.
    const todayKey = dayjs().tz(userTimezone).format('YYYY-MM-DD');
    const gameLocalKey = game.date_time
      ? dayjs.tz(game.date_time, 'UTC').tz(userTimezone).format('YYYY-MM-DD')
      : null;
    const isToday = !!gameLocalKey && gameLocalKey === todayKey && isActive;

    // CTA config — active runs keep prior behavior. Non-active runs never expose
    // Join/Full; awaiting_outcome surfaces "Submit Game Outcome" for the host
    // (host precedence even if also a player).
    let cta;
    if (invitation) {
      const unavailable = !invitation.can_accept || invitation.is_full || rejected;
      cta = { label: rejected || invitation.is_full ? 'Full' : !invitation.can_accept ? 'Capacity unavailable' : `Join ${runNoun}`,
        style: unavailable ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed' : 'bg-zinc-900 text-white hover:bg-zinc-800',
        disabled: unavailable || Boolean(invitations.busyId) };
      if (invitations.busyId === invitation.id) cta.label = 'Joining�';
    } else if (lifecycle === RUN_LIFECYCLE.CANCELLED) {
      // Status label ("Cancelled") shows in the meta row; keep a USABLE CTA so
      // users can open the run and read the cancellation details. No Join/spots.
      cta = { label: 'View Details', style: 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200', disabled: false };
    } else if (lifecycle === RUN_LIFECYCLE.COMPLETED) {
      cta = { label: hasResult ? 'View Results' : `View ${runNoun}`, style: 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200', disabled: false };
    } else if (lifecycle === RUN_LIFECYCLE.AWAITING_OUTCOME) {
      cta = isCreator
        ? { label: 'Submit Game Outcome', style: 'bg-orange-500 text-white hover:bg-orange-600', disabled: false }
        : isCoHost
          ? { label: `Manage ${runNoun}`, style: 'bg-orange-500 text-white hover:bg-orange-600', disabled: false }
        : { label: `View ${runNoun}`, style: 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200', disabled: false };
    } else if (isManagedByUser) {
      cta = { label: `Manage ${runNoun}`, style: 'bg-orange-500 text-white hover:bg-orange-600', disabled: false };
    } else if (isJoined) {
      cta = { label: `You're In This ${runNoun}`, joined: true, disabled: false };
    } else if (isFull) {
      cta = { label: 'Full', style: 'bg-zinc-100 text-zinc-400 cursor-not-allowed', disabled: true };
    } else {
      cta = { label: `Join ${runNoun}`, style: 'bg-zinc-900 text-white hover:bg-zinc-800', disabled: false };
    }
    
    return (
      <div
        className={`bg-white px-5 py-4 rounded-xl transition-all duration-150
          ${isToday
            ? 'border-y border-r border-l-4 border-l-orange-500 border-y-zinc-200/80 border-r-zinc-200/80 shadow-md shadow-orange-100/60'
            : 'border border-zinc-200/80'
          }
          ${isAgeRestricted ? 'opacity-60' : ''}`}
        data-testid={`game-card-${game.id}`}
        data-invited={invitation ? "true" : undefined}
        data-today={isToday ? 'true' : 'false'}
      >
        <div className="cursor-pointer" onClick={() => !invitation && navigate(`/games/${game.id}`)}>
          {/* Date/time + optional TODAY pill */}
          <div className="flex items-center gap-2 mb-1">
            <p className="text-[13px] font-semibold text-orange-600 tracking-wide">
              {formatCardDateTime(game.date_time)}
            </p>
            {invitation && <span className="bg-orange-100 text-orange-700 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded leading-none">Invited</span>}
            {isToday && (
              <span
                className="bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded leading-none"
                data-testid="today-pill"
              >
                Today
              </span>
            )}
            {game.court_type === 'training' && (
              <span
                className="bg-orange-100 text-orange-700 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded leading-none"
                data-testid={`training-badge-${game.id}`}
              >
                Training
              </span>
            )}
          </div>
          {/* Title */}
          <h3 className="text-[17px] font-bold text-zinc-900 leading-tight mb-1.5 truncate">
            {game.title}
          </h3>
          {/* Location */}
          <div className="flex items-center gap-1.5 mb-1.5">
            <MapPin className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />
            <p className="text-[15px] text-zinc-700 truncate">{game.location_name}</p>
          </div>
          {/* Host */}
          <p className="text-sm text-zinc-600 mb-3 truncate">
            Hosted by <span className="font-medium text-zinc-800">{hostName}</span>
          </p>
          {coHosts.length > 0 && (
            <p className="text-sm text-zinc-600 mb-3 truncate">
              Co-hosts:{' '}
              <span className="font-medium text-zinc-800">
                {coHosts.map(coHost => coHost.display_name || coHost.username).filter(Boolean).join(', ')}
              </span>
            </p>
          )}
          {/* Meta row: status/spots + age */}
          <div className="flex items-center gap-3 text-sm text-zinc-500 mb-4">
            <span
              className={`font-medium ${!isActive ? 'text-zinc-500' : isFull ? 'text-zinc-400' : spotsLeft <= 3 ? 'text-orange-600' : 'text-zinc-700'}`}
              data-testid={`run-status-label-${game.id}`}
            >
              {invitation ? rejected || invitation.is_full ? 'Full' : !invitation.can_accept ? 'Capacity unavailable' : Number.isFinite(invitation.max_players) && Number.isFinite(invitation.participant_count) ? `${Math.max(0, invitation.max_players - invitation.participant_count)} spots left` : 'Invited' : isActive ? spotsText : lifecycleLabel}
            </span>
            {isActive && isAgeRestricted && (
              <>
                <span className="text-zinc-300">|</span>
                <span className="flex items-center gap-0.5">
                  <Lock className="w-3 h-3" /> {formatAgeRange(gameAgeMin, gameAgeMax)}
                </span>
              </>
            )}
          </div>
        </div>

        {rejected && <p role="status" className="text-sm text-zinc-600 mb-3">This session is currently full. Refresh invitations to check again.</p>}
        {/* CTA */}
        {cta.joined ? (
          /* Joined status — positive, NOT a disabled button. Whole pill taps
             through to the run/training detail (same as the card), so there is
             no ambiguity about join/leave. Text + color + checkmark. */
          <button
            onClick={() => navigate(`/games/${game.id}`)}
            className="w-full h-10 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-all duration-150 active:scale-[0.98]"
            data-testid={`joined-status-${game.id}`}
          >
            <CheckCircle2 className="w-4 h-4" />
            {cta.label}
          </button>
        ) : (
          <button
            onClick={() => !cta.disabled && (invitation ? invitations.accept(invitation) : navigate(`/games/${game.id}`))}
            disabled={cta.disabled}
            className={`w-full h-10 rounded-xl text-sm font-semibold transition-all duration-150 active:scale-[0.98] ${cta.style}`}
            data-testid={`cta-${game.id}`}
          >
            {cta.label}
          </button>
        )}
      </div>
    );
  };

  // Section header component
  const SectionHeader = ({ title, count, icon: Icon }) => (
    <div className="flex items-center gap-2 px-1 pt-4 pb-1">
      <Icon className="w-4 h-4 text-zinc-600" />
      <h2 className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">{title}</h2>
      <span className="text-xs text-zinc-500">({count})</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24">
      {/* TZ Debug Banner */}
      {debugTz && (
        <div className="fixed bottom-4 left-4 bg-yellow-100 border-2 border-yellow-400 p-2 rounded-lg shadow-lg z-50 text-xs font-mono max-w-xs">
          <div className="font-bold text-yellow-800 mb-1">TZ Debug</div>
          <div className="text-yellow-900 space-y-0.5">
            <div>Browser: {getUserTimezone()}</div>
            {previewTz && (
              <>
                <div>Preview: {previewTz}</div>
                <div className="text-yellow-700 text-[10px]">Add ?tz=America/Chicago to URL</div>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Header */}
      <header
        className="dashboard-desktop-header sticky top-0 z-30 bg-[#FFFFFF] border-b border-[#E4E4E7]"
        style={{ paddingTop: 'max(0.625rem, env(safe-area-inset-top))' }}
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-2.5 flex items-center justify-between">
          <div className="flex items-center gap-3 flex-shrink-0">
            <img 
              src="/logo-transparent.png" 
              alt="Open Runs USA" 
              className="w-[110px] sm:w-[160px] h-auto"
              style={{ minWidth: '110px' }}
              data-testid="header-logo"
            />
          </div>
          <nav className="flex items-center gap-1 sm:gap-2">
            <NotificationBell desktopOnly />
            <Button
              variant="ghost"
              onClick={() => navigate('/host')}
              className="h-10 px-3 text-[14px] font-medium text-[#52525B] hover:text-[#111111] hover:bg-[#FAFAFA] rounded-[12px] gap-2"
              data-testid="host-btn"
            >
              <Briefcase className="w-4 h-4" />
              <span className="hidden sm:inline">Host</span>
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/my-clubs')}
              className="h-10 px-3 text-[14px] font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-[12px] gap-2"
              data-testid="clubs-btn"
            >
              <Users2 className="w-4 h-4" />
              <span className="hidden sm:inline">Clubs</span>
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/profile')}
              className="h-10 px-3 text-[14px] font-medium text-[#52525B] hover:text-[#111111] hover:bg-[#FAFAFA] rounded-[12px] gap-2"
              data-testid="profile-btn"
            >
              <User className="w-4 h-4" />
              <span className="hidden sm:inline">{user?.username}</span>
            </Button>
            {(user?.role === 'scout' || user?.role === 'admin') && (
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="h-10 px-3 text-[14px] font-medium text-[#52525B] hover:text-[#111111] hover:bg-[#FAFAFA] rounded-[12px] gap-2"
                data-testid="admin-btn"
              >
                <Shield className="w-4 h-4" />
                <span className="hidden sm:inline">Admin</span>
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => navigate('/support')}
              className="h-10 px-3 text-[14px] font-medium text-[#52525B] hover:text-[#111111] hover:bg-[#FAFAFA] rounded-[12px] gap-2"
              data-testid="help-btn"
            >
              <HelpCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Help</span>
            </Button>
            <Button
              variant="ghost"
              onClick={logout}
              className="h-10 px-3 text-[14px] font-medium text-[#52525B] hover:text-[#111111] hover:bg-[#FAFAFA] rounded-[12px] gap-2"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </nav>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <IOSInstallCTA authenticated={Boolean(user)} placement="dashboard" />
        {/* Content-First Header: Title + Actions */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-[24px] sm:text-[32px] font-bold text-[#111111] tracking-[-0.02em]">
            {viewMode === 'feed' ? 'Your Feed' : 'Discover Runs'}
          </h1>
          <div className="flex items-center gap-2">
            {/* View Toggle */}
            <div className="hidden sm:flex items-center bg-zinc-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('feed')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'feed' 
                    ? 'bg-white text-zinc-900 shadow-sm' 
                    : 'text-zinc-600 hover:text-zinc-900'
                }`}
                data-testid="feed-toggle"
              >
                My Feed
              </button>
              <button
                onClick={() => setViewMode('discover')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'discover' 
                    ? 'bg-white text-zinc-900 shadow-sm' 
                    : 'text-zinc-600 hover:text-zinc-900'
                }`}
                data-testid="discover-toggle"
              >
                Discover
              </button>
            </div>

            {/* Filter Button - only show in discover mode */}
            {viewMode === 'discover' && (
              <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-9 px-3 border-[#E4E4E7] text-[#52525B] hover:bg-[#FAFAFA] rounded-[12px] text-[13px] font-medium gap-2"
                  >
                    <SlidersHorizontal className="w-4 h-4" />
                    Filter
                    {activeFilterCount > 0 && (
                      <span className="ml-1 w-5 h-5 bg-[#111111] text-white text-[11px] font-semibold rounded-full flex items-center justify-center">
                        {activeFilterCount}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="rounded-t-[24px] max-h-[85vh] overflow-y-auto">
                  <SheetHeader className="mb-6">
                    <SheetTitle className="text-[20px] font-bold text-[#111111] tracking-[-0.02em]">Filters</SheetTitle>
                  </SheetHeader>
                  
                  <div className="space-y-6">
                    {/* Location */}
                    <div>
                      <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[#71717A] mb-2">Location</p>
                      <LocationSearchInput
                        value={locInput}
                        onTyping={(v) => { 
                          setLocInput(v); 
                          setNear(null); 
                        }}
                        onSelect={(p) => { 
                          setNear(p); 
                          setLocInput(p.label); 
                        }}
                        placeholder="City or address"
                      />
                    </div>

                    {/* Radius */}
                    <div>
                      <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[#71717A] mb-2">Radius</p>
                      <Select
                        value={radiusKm.toString()}
                        onValueChange={(value) => setRadiusKm(Number(value))}
                      >
                        <SelectTrigger className="h-10 rounded-[12px] border-[#E4E4E7]">
                          <SelectValue placeholder="Radius" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10 mi</SelectItem>
                          <SelectItem value="25">25 mi</SelectItem>
                          <SelectItem value="50">50 mi</SelectItem>
                          <SelectItem value="100">100 mi</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Skill Level */}
                    <div>
                      <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[#71717A] mb-2">Skill Level</p>
                      <Select
                        value={filters.skill_level}
                        onValueChange={(value) => setFilters({ ...filters, skill_level: value })}
                      >
                        <SelectTrigger data-testid="filter-skill-select" className="h-10 rounded-[12px] border-[#E4E4E7]">
                          <SelectValue placeholder="All Levels" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Levels</SelectItem>
                          <SelectItem value="Unranked">Unranked</SelectItem>
                          <SelectItem value="Role Player">Role Player</SelectItem>
                          <SelectItem value="Baller">Baller</SelectItem>
                          <SelectItem value="Elite">Elite</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Run Type */}
                    <div>
                      <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[#71717A] mb-2">Run Type</p>
                      <Select
                        value={filters.court_type}
                        onValueChange={(value) => setFilters({ ...filters, court_type: value })}
                      >
                        <SelectTrigger data-testid="filter-court-type-select" className="h-10 rounded-[12px] border-[#E4E4E7]">
                          <SelectValue placeholder="All Runs" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Runs</SelectItem>
                          <SelectItem value="full_court">Full Court</SelectItem>
                          <SelectItem value="half_court">Half Court</SelectItem>
                          <SelectItem value="training">Training</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Status */}
                    <div>
                      <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[#71717A] mb-2">Status</p>
                      <Select
                        value={filters.status}
                        onValueChange={(value) => setFilters({ ...filters, status: value })}
                      >
                        <SelectTrigger data-testid="filter-status-select" className="h-10 rounded-[12px] border-[#E4E4E7]">
                          <SelectValue placeholder="Open" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="full">Full</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Apply Button */}
                    <Button
                      onClick={() => setFilterOpen(false)}
                      className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-[12px] text-[15px] font-semibold"
                    >
                      Apply Filters
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            )}

            {/* Create Run — labeled CTA, visible on all viewports.
                Matches the Host Dashboard button's visual language so the
                Create affordance reads identically across surfaces. */}
            <Button
              onClick={() => navigate('/create-game')}
              className="h-10 px-5 bg-orange-500 hover:bg-orange-600 text-white rounded-[12px] text-[13px] font-semibold gap-2"
              data-testid="create-game-btn"
            >
              <Plus className="w-4 h-4" />
              Create Run
            </Button>
          </div>
        </div>

        {/* Mobile View Toggle */}
        <div className="flex sm:hidden items-center bg-zinc-100 rounded-lg p-1 mb-4">
          <button
            onClick={() => setViewMode('feed')}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'feed' 
                ? 'bg-white text-zinc-900 shadow-sm' 
                : 'text-zinc-600 hover:text-zinc-900'
            }`}
          >
            My Feed
          </button>
          <button
            onClick={() => setViewMode('discover')}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'discover' 
                ? 'bg-white text-zinc-900 shadow-sm' 
                : 'text-zinc-600 hover:text-zinc-900'
            }`}
          >
            Discover
          </button>
        </div>

        {/* Date Strip — Today + next 4 days */}
        <div className="dashboard-date-strip flex gap-2 mb-5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide" data-testid="date-strip">
          <button
            onClick={() => setSelectedDate(null)}
            className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-150 active:scale-95
              ${selectedDate === null
                ? 'bg-zinc-900 text-white shadow-sm'
                : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
            data-testid="date-all"
          >
            All
          </button>
          {dateTiles.map(tile => (
            <button
              key={tile.key}
              onClick={() => setSelectedDate(selectedDate === tile.key ? null : tile.key)}
              className={`flex-shrink-0 flex flex-col items-center px-4 py-2 rounded-xl transition-all duration-150 active:scale-95
                ${selectedDate === tile.key
                  ? 'bg-zinc-900 text-white shadow-sm'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                }`}
              data-testid={`date-${tile.key}`}
            >
              <span className="text-[11px] font-semibold uppercase leading-none">{tile.label}</span>
              <span className="text-lg font-bold leading-tight">{tile.date}</span>
            </button>
          ))}
        </div>

        {viewMode === 'feed' && !loading && <>
          <PersistentPushAction />
          <PushReminderSlot />
        </>}

        {/* Location hint for personalized feed.
            !loading guard prevents the tip from flashing while feedData still
            holds stale games from a previous Discover-mode fetch (where
            user_location_set is hardcoded to false at line 145). Matches the
            !loading guard pattern used for <PeopleToFollow /> below. */}
        {viewMode === 'feed' && !loading && !feedData.user_location_set && feedData.games.length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-sm text-blue-800">
              <MapPin className="w-4 h-4 inline mr-1" />
              <strong>Tip:</strong> Add your address in{' '}
              <button 
                onClick={() => navigate('/profile')} 
                className="underline hover:no-underline"
              >
                Profile Settings
              </button>
              {' '}to see distances to runs.
            </p>
          </div>
        )}

        {/* People to Follow — top carousel intentionally hidden to prioritize
            My Games/feed content. Reversible: re-enable by uncommenting below.
            (Lower "Players You May Know" / <PlayerSuggestions /> remains the single
            follow-suggestion module on this page.) */}
        {/* {viewMode === 'feed' && !loading && <PeopleToFollow />} */}

        {viewMode === 'feed' && <div className="flex flex-wrap items-center gap-3 mb-4">
          <button className="text-sm font-semibold underline" onClick={() => { setSelectedDate(null); setInvitationFocus(true); }}>Pending Invitations ({invitations.rows.length})</button>
          <button className="text-sm underline" onClick={() => invitations.refresh(true)}>Refresh invitations</button>
          {invitations.loading && <span role="status">Loading invitations�</span>}
          {invitations.error && <span role="alert">{invitations.error}</span>}
        </div>}
        {/* Games Feed */}
        {loading && displayGames.length === 0 ? (
          <div className="space-y-4" data-testid="feed-skeleton">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-xl border border-zinc-200 shadow-[0_1px_2px_rgba(0,0,0,0.04)] px-6 pt-5 pb-6 animate-pulse">
                <div className="h-4 bg-zinc-200 rounded w-2/5 mb-3" />
                <div className="h-6 bg-zinc-200 rounded w-4/5 mb-2" />
                <div className="h-4 bg-zinc-200 rounded w-3/5 mb-4" />
                <div className="flex gap-4 mb-4 pt-2 border-t border-zinc-100">
                  <div className="h-4 bg-zinc-200 rounded w-24" />
                  <div className="h-4 bg-zinc-200 rounded w-20" />
                </div>
                <div className="h-2 bg-zinc-200 rounded-full w-full mb-4" />
                <div className="h-10 bg-zinc-200 rounded-xl w-full" />
              </div>
            ))}
          </div>
        ) : displayGames.length === 0 && selectedDate ? (
          /* Empty state when date filter is active */
          <div className="text-center py-16">
            <p className="text-zinc-500 text-base mb-2">No runs scheduled for this day.</p>
            <Button
              onClick={() => setSelectedDate(null)}
              variant="outline"
              className="h-10 px-6 rounded-full text-sm font-medium border-zinc-300 hover:bg-zinc-50 transition-all active:scale-95"
            >
              Show All Runs
            </Button>
          </div>
        ) : displayGames.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#71717A] text-[16px] mb-4">No runs found</p>
            <Button 
              onClick={() => navigate('/create-game')} 
              className="h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-[12px] px-6 text-[15px] font-semibold"
            >
              Create the First Run
            </Button>
          </div>
        ) : viewMode === 'feed' ? (
          /* Personalized Feed View */
          <div className="space-y-4" data-testid="games-feed">
            {/* My Games Section */}
            {myGames.length > 0 && (
              <>
                <SectionHeader title="My Games / Managed by Me" count={myGames.length} icon={ListChecks} />
                {myGames.map((game) => (
                  <GameCard key={game.id} game={game} />
                ))}
              </>
            )}

            {invitedGames.length > 0 && <div className="space-y-4" data-testid="invitations-group">
              <SectionHeader title="Invitations" count={invitedGames.length} icon={Users} />
              {invitedGames.map(game => <GameCard key={game.id} game={game} />)}
            </div>}

            {/* Following Section - only show if user follows someone and has games */}
            {followingGames.length > 0 && (
              <>
                <SectionHeader title="From People You Follow" count={followingGames.length} icon={Users} />
                {followingGames.map((game) => (
                  <GameCard key={game.id} game={game} />
                ))}
              </>
            )}

            {/* Discover Section */}
            {discoverGames.length > 0 && (
              <>
                <SectionHeader 
                  title={feedData.user_location_set ? "Runs Near You" : "Other Runs"} 
                  count={discoverGames.length} 
                  icon={MapPin} 
                />
                {discoverGames.map((game) => (
                  <GameCard key={game.id} game={game} />
                ))}
              </>
            )}

            {/* Empty state for feed with no games in any section */}
            {myGames.length === 0 && invitedGames.length === 0 && followingGames.length === 0 && discoverGames.length === 0 && (
              <div className="text-center py-16">
                <p className="text-[#71717A] text-[16px] mb-4">No upcoming runs</p>
                <Button 
                  onClick={() => navigate('/create-game')} 
                  className="h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-[12px] px-6 text-[15px] font-semibold"
                >
                  Create a Run
                </Button>
              </div>
            )}
          </div>
        ) : (
          /* Discover/Filtered View */
          <div className="space-y-4" data-testid="games-grid">
            {displayGames.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </div>
        )}
        


        {/* One mounted instance shares recommendations and follow state across tabs. */}
        {user && (
          <div className="mt-6">
            <PlayerSuggestions visible={showRecommendations} maxItems={viewMode === 'feed' ? 3 : undefined} />
          </div>
        )}
      </div>
      <Footer variant="slim" />
    </div>
  );
};

export default Dashboard;
